// src/pages/Login.tsx - Organizasyon kodu validation mesajları kaldırılmış

import React, { useState, useEffect, useRef } from 'react'
import { motion } from 'framer-motion'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card'
import { LogIn, User, Mail, Lock, Eye, EyeOff, Gift, Shield, Zap, ArrowRight, Building2, Phone, ChevronDown } from 'lucide-react'
import toast, { Toaster } from 'react-hot-toast'
import { useAuth } from '../contexts/AuthContext'
import { useNavigate, useLocation } from 'react-router-dom'

const COUNTRIES = [
  { code: '+90', cc: 'tr', name: 'Türkiye' },
  { code: '+1', cc: 'us', name: 'ABD' },
  { code: '+44', cc: 'gb', name: 'İngiltere' },
  { code: '+49', cc: 'de', name: 'Almanya' },
]
const flagUrl = (cc: string) => `https://flagcdn.com/24x18/${cc}.png`

export default function LoginPage() {
  const { login, register, isLoading } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  // /register => kayıt formu, /login ve diğer her yol => giriş formu
  const isLogin = location.pathname !== '/register'
  // Referans linki ile gelindiyse: earnbackhub.com/register?ref=KOD
  const refParam = new URLSearchParams(location.search).get('ref')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [fullName, setFullName] = useState('')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [countryCode, setCountryCode] = useState('+90')
  const [countryOpen, setCountryOpen] = useState(false)
  const countryRef = useRef<HTMLDivElement>(null)
  const selectedCountry = COUNTRIES.find((c) => c.code === countryCode) || COUNTRIES[0]
  const [showPassword, setShowPassword] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  
  // Organizasyon kodu - artık validation yok
  const [organizationCode, setOrganizationCode] = useState('')

  // Referans linki ile gelindiyse referans alanını otomatik doldur
  useEffect(() => {
    if (refParam) setOrganizationCode(refParam.toUpperCase())
  }, [refParam])

  // Ülke dropdown'ı dışına tıklayınca kapat
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (countryRef.current && !countryRef.current.contains(e.target as Node)) setCountryOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [])

  // Telefon: ülke kodlu ve tam olmalı. Desteklenen: Türkiye (+90), ABD (+1), İngiltere (+44), Almanya (+49)
  const normalizePhone = (raw: string) => raw.replace(/[\s\-().]/g, '')
  const isValidPhone = (raw: string) =>
    /^(\+90\d{10}|\+1\d{10}|\+44\d{9,10}|\+49\d{9,11})$/.test(normalizePhone(raw))

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Ülke kodu (dropdown) + girilen numara birleşir; baştaki 0(lar) atılır (TR 0532 → 532)
    const fullPhone = countryCode + phoneNumber.replace(/\D/g, '').replace(/^0+/, '')

    if (!email || !password || (!isLogin && (!fullName || !phoneNumber))) {
      toast.error('Lütfen tüm alanları doldurun')
      return
    }

    if (!isLogin && !isValidPhone(fullPhone)) {
      toast.error('Lütfen geçerli bir telefon numarası girin (ülke kodunu seçip numaranızı baştaki 0 olmadan yazın).')
      return
    }

    if (password.length < 6) {
      toast.error('Şifre en az 6 karakter olmalıdır')
      return
    }

    setIsSubmitting(true)

    try {
      if (isLogin) {
        await login(email, password)
      } else {
        // Organizasyon kodu ile kayıt - backend'de kontrol edilecek
        await register(email, fullName, password, normalizePhone(fullPhone), organizationCode || undefined)
      }
    } catch (error) {
      // Error handling is done in AuthContext
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleGoogleLogin = () => {
    toast.error('Google ile giriş henüz desteklenmiyor. Lütfen e-posta ile giriş yapın.')
  }

  const handleForgotPassword = () => {
    toast.success('Şifre sıfırlama özelliği yakında eklenecek.')
  }

  const toggleMode = () => {
    navigate(isLogin ? '/register' : '/login')
    setEmail('')
    setPassword('')
    setFullName('')
    setShowPassword(false)
    setOrganizationCode('')
    setPhoneNumber('')
    setCountryCode('+90')
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden">
      <Toaster position="top-center" />
      
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary-500/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-orange-500/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-60 h-60 bg-green-500/10 rounded-full blur-3xl animate-pulse delay-2000"></div>
      </div>

      <div className="flex flex-col lg:flex-row items-center justify-center gap-12 relative z-10 w-full max-w-6xl">
        {/* Left side - Branding */}
        <motion.div 
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center lg:text-left lg:w-1/2"
        >
           {/* Header with Logo */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-3 mb-3">
              <img 
                src="/logo512.png" 
                alt="EarnBackHub Logo" 
                className="w-16 h-16 object-contain"
              />
              <h1 className="text-4xl font-bold gradient-text">EarnBackHub</h1>
            </div>
            <p className="text-lg text-gray-600">
              Amazon siparişleninizden bonus kazanın
            </p>
          </div>

          {/* Features */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="glass rounded-xl p-6 text-center border border-orange-200 hover:border-orange-300 transition-all">
              <Gift className="w-8 h-8 text-orange-500 mx-auto mb-3" />
              <h3 className="font-semibold text-gray-800 mb-2">Ödüller</h3>
              <p className="text-sm text-gray-600">Amazonda verilen her siparişinizden bonuslar kazanın.</p>
            </div>
            
            <div className="glass rounded-xl p-6 text-center border border-orange-200 hover:border-orange-300 transition-all">
              <Shield className="w-8 h-8 text-orange-500 mx-auto mb-3" />
              <h3 className="font-semibold text-gray-800 mb-2">Güvenli</h3>
              <p className="text-sm text-gray-600">SSL şifreli güvenli bağlantı</p>
            </div>
            
            <div className="glass rounded-xl p-6 text-center border border-orange-200 hover:border-orange-300 transition-all">
              <Zap className="w-8 h-8 text-orange-500 mx-auto mb-3" />
              <h3 className="font-semibold text-gray-800 mb-2">Hızlı</h3>
              <p className="text-sm text-gray-600">Anında bonus hesaplama</p>
            </div>
            
            <div className="glass rounded-xl p-6 text-center border border-orange-200 hover:border-orange-300 transition-all">
              <ArrowRight className="w-8 h-8 text-orange-500 mx-auto mb-3" />
              <h3 className="font-semibold text-gray-800 mb-2">Kolay</h3>
              <p className="text-sm text-gray-600">Basit ve kullanıcı dostu</p>
            </div>
          </div>

          <div className="text-sm text-gray-500">
            <p></p>
            <p></p>
            <p></p>
          </div>
        </motion.div>

        {/* Right side - Login Form */}
        <motion.div 
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="w-full lg:w-1/2 max-w-md"
        >
          <Card className="glass border-orange-200 shadow-xl">
            <CardHeader className="text-center">
              <CardTitle className="text-2xl font-bold text-gray-800">
                {isLogin ? 'Hoş Geldiniz' : 'Hesap Oluştur'}
              </CardTitle>
              <CardDescription className="text-gray-600">
                {isLogin ? 'Hesabınıza giriş yapın' : 'Yeni hesap oluşturun'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                {!isLogin && (
                  <div className="relative">
                    <User className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <Input
                      type="text"
                      placeholder="Ad Soyad"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      className="pl-10 border-gray-200 focus:border-orange-500 focus:ring-orange-500"
                      required={!isLogin}
                      disabled={isSubmitting || isLoading}
                    />
                  </div>
                )}

                {!isLogin && (
  <div className="flex gap-2">
    <div className="relative" ref={countryRef}>
      <button
        type="button"
        onClick={() => setCountryOpen((o) => !o)}
        disabled={isSubmitting || isLoading}
        className="flex items-center gap-2 h-full border border-gray-200 rounded-md px-2.5 py-2 text-sm bg-white hover:bg-gray-50 focus:border-orange-500 focus:outline-none disabled:opacity-50"
        aria-label="Ülke kodu"
      >
        <img src={flagUrl(selectedCountry.cc)} alt="" width={24} height={18} className="rounded-sm shrink-0" />
        <span className="text-gray-700">({selectedCountry.code})</span>
        <ChevronDown className="w-4 h-4 text-gray-400" />
      </button>
      {countryOpen && (
        <div className="absolute z-20 mt-1 w-56 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
          {COUNTRIES.map((c) => (
            <button
              key={c.code}
              type="button"
              onClick={() => { setCountryCode(c.code); setCountryOpen(false) }}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-orange-50 text-left"
            >
              <img src={flagUrl(c.cc)} alt="" width={24} height={18} className="rounded-sm shrink-0" />
              <span className="text-gray-700 w-12 shrink-0">({c.code})</span>
              <span className="text-gray-600">{c.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
    <div className="relative flex-1">
      <Phone className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
      <Input
        type="tel"
        placeholder="Telefon numarası"
        value={phoneNumber}
        onChange={(e) => setPhoneNumber(e.target.value.replace(/^0+/, ''))}
        className="pl-10 border-gray-200 focus:border-orange-500 focus:ring-orange-500"
        required={!isLogin}
        disabled={isSubmitting || isLoading}
      />
    </div>
  </div>
)}

                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    type="email"
                    placeholder="E-posta adresiniz"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 border-gray-200 focus:border-orange-500 focus:ring-orange-500"
                    required
                    disabled={isSubmitting || isLoading}
                  />
                </div>

                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    type={showPassword ? "text" : "password"}
                    placeholder="Şifreniz"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 pr-10 border-gray-200 focus:border-orange-500 focus:ring-orange-500"
                    required
                    disabled={isSubmitting || isLoading}
                    minLength={6}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-3 text-gray-400 hover:text-gray-600 disabled:opacity-50"
                    disabled={isSubmitting || isLoading}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>

                {/* Organizasyon Kodu - SADECE input, validation yok */}
                {!isLogin && (
                  <div className="space-y-2">
                    <div className="relative">
                      <Building2 className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                      <Input
                        type="text"
                        placeholder="Referans Kodu (Opsiyonel)"
                        value={organizationCode}
                        onChange={(e) => setOrganizationCode(e.target.value.toUpperCase())}
                        className={`pl-10 border-gray-200 focus:border-orange-500 focus:ring-orange-500 font-mono uppercase ${refParam ? 'bg-gray-100 text-gray-600 cursor-not-allowed' : ''}`}
                        disabled={isSubmitting || isLoading}
                        readOnly={!!refParam}
                      />
                    </div>
                    <p className={`text-xs pl-1 ${refParam ? 'text-green-600' : 'text-gray-500'}`}>
                      {refParam
                        ? 'Referans kodunuz hazır ✓'
                        : 'Referans kodunuz varsa buraya girebilirsiniz.'}
                    </p>
                  </div>
                )}

                {isLogin && (
                  <div className="flex items-center justify-between text-sm">
                    <label className="flex items-center space-x-2">
                      <input 
                        type="checkbox" 
                        className="rounded border-orange-300" 
                        disabled={isSubmitting || isLoading}
                      />
                      <span className="text-gray-600">Beni Hatırla</span>
                    </label>
                    <button 
                      type="button" 
                      className="text-orange-600 hover:text-orange-700 transition-colors disabled:opacity-50"
                      onClick={handleForgotPassword}
                      disabled={isSubmitting || isLoading}
                    >
                      Şifreni mi unuttun?
                    </button>
                  </div>
                )}

                <Button 
                  type="submit" 
                  className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-medium"
                  disabled={isSubmitting || isLoading}
                >
                  {isSubmitting || isLoading ? (
                    <div className="flex items-center">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      {isLogin ? 'Giriş yapılıyor...' : 'Hesap oluşturuluyor...'}
                    </div>
                  ) : (
                    <>
                      <LogIn className="w-4 h-4 mr-2" />
                      {isLogin ? 'Oturum Aç' : 'Hesap Oluştur'}
                    </>
                  )}
                </Button>

                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t border-gray-200" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-white px-2 text-gray-500">veya</span>
                  </div>
                </div>

                <Button 
                  variant="outline" 
                  className="w-full border-gray-200 hover:bg-gray-50 hover:border-orange-300"
                  onClick={handleGoogleLogin}
                  type="button"
                  disabled={isSubmitting || isLoading}
                >
                  <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
                    <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Google ile devam et
                </Button>
              </form>

              <div className="mt-6 text-center">
                <p className="text-sm text-gray-600">
                  {isLogin ? "Hesabınız yok mu?" : "Zaten hesabınız var mı?"}
                  <button
                    onClick={toggleMode}
                    className="ml-1 text-orange-600 hover:text-orange-700 font-medium transition-colors disabled:opacity-50"
                    disabled={isSubmitting || isLoading}
                  >
                    {isLogin ? 'Hesap Oluştur' : 'Giriş Yap'}
                  </button>
                </p>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  )
}